import { Button, Group, Stack, Text } from '@mantine/core';
import { useConsoleNav } from '../shell/nav';
import { AutoGrid, EstatePill, KV, NotConnected, PageHeader, Pill, SectionCard } from '../ui';
import { useEstatesStore, useTenantsStore } from '../stores/dataStores';
import { isFirstLoad, isUnconfigured, rowsOf } from '../stores/collection';
import { useUIStore } from '../stores/uiStore';

export function EstatesPage() {
  const navigate = useConsoleNav();
  const estates = useEstatesStore((s) => s.data);
  const tenants = rowsOf(useTenantsStore((s) => s.data));
  const openModal = useUIStore((s) => s.openModal);
  const setUI = useUIStore((s) => s.set);

  // The Shell already watches estates for the sidebar; this page reads the same
  // store rather than opening a second watch over the same collection.

  const rows = rowsOf(estates);

  return (
    <Stack gap={16}>
      <PageHeader
        title="Estates"
        lede="A product platform, not a customer. The estate owns realms, provider policy, pool scope, rate limits and the billing envelope lane — and it is the level that makes “a caller can only ever affect its own estate” a property of the credential rather than a validation rule."
        actions={
          <Button onClick={() => openModal({ kind: 'newEstate' })}>New estate</Button>
        }
      />

      {rows.length === 0 ? (
        <NotConnected
          what={isFirstLoad(estates) ? 'loading estates' : 'the estate registry'}
          detail={
            isUnconfigured(estates)
              ? 'router_estates is watchable from a browser once Firestore rules admit a superadmin, but those rules are not written yet.'
              : undefined
          }
        />
      ) : (
        <AutoGrid min={330} gap={16}>
          {rows.map((e) => {
            const count = tenants.filter((t) => t.estateId === e.id).length;
            return (
              <SectionCard key={e.id} p={20}>
                <Group gap={10} wrap="nowrap">
                  <EstatePill estate={e.id} size="lg" />
                  <Text fz={18} fw={800} truncate>
                    {e.displayName}
                  </Text>
                  <Group style={{ marginLeft: 'auto' }}>
                    <Pill tone={e.enabled ? 'ok' : 'neutral'} size="lg">
                      {e.enabled ? 'ENABLED' : 'DISABLED'}
                    </Pill>
                  </Group>
                </Group>
                <Stack gap={11} mt={16}>
                  <KV k="Realms" v={e.realms.map((r) => r.name).join(', ')} />
                  <KV k="Providers" v={(e.providers.allowed ?? []).join(', ') || '—'} />
                  <KV
                    k="Pool scope"
                    v={
                      e.pool.reserved && e.pool.reserved.length
                        ? `shared + ${e.pool.reserved.length} reserved`
                        : 'shared only'
                    }
                  />
                  <KV k="Envelope lane" v={e.billing.eventLane} />
                  <KV
                    k="Limits"
                    v={`${e.limits.billablePerMinute} billable / ${e.limits.attributedPerMinute} attributed per min`}
                  />
                  <KV k="Tenants" v={count ? `${count} active` : '—'} />
                </Stack>
                <Group gap={8} mt={18}>
                  <Button
                    variant="light"
                    size="xs"
                    onClick={() => {
                      setUI('estateTab', 'policy');
                      navigate(`/estates/${e.id}`);
                    }}
                  >
                    Open estate
                  </Button>
                  <Button
                    variant="default"
                    size="xs"
                    onClick={() => {
                      setUI('estateTab', 'settings');
                      navigate(`/estates/${e.id}`);
                    }}
                  >
                    Settings
                  </Button>
                  <Button
                    variant="default"
                    size="xs"
                    onClick={() => {
                      setUI('estateTab', 'tenants');
                      navigate(`/estates/${e.id}`);
                    }}
                  >
                    {count} tenants
                  </Button>
                </Group>
              </SectionCard>
            );
          })}
        </AutoGrid>
      )}
    </Stack>
  );
}
